import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react"
import type { AppShell } from "@/App"
import { producaoApi, type MaterialState } from "./api"

/**
 * Estado do material por obra, partilhado entre quadro, semana e detalhe.
 * Pede ao servidor (que tem cache curta) com no máximo 4 pedidos em paralelo:
 * o quadro pede isto para muitas obras.
 */
interface Ctx {
  get(jobId: string): MaterialState | undefined
  request(jobId: string, opts?: { fresh?: boolean }): void
}

const MaterialCtx = createContext<Ctx | null>(null)
const CONCURRENCY = 4

export function MaterialProvider({ shell, children }: { shell: AppShell | null; children: ReactNode }) {
  const [states, setStates] = useState<Record<string, MaterialState>>({})
  const queue = useRef<Array<{ id: string; fresh: boolean }>>([])
  const inFlight = useRef(new Set<string>())
  const requested = useRef(new Set<string>())

  const pump = useCallback(() => {
    if (!shell) return
    while (inFlight.current.size < CONCURRENCY && queue.current.length > 0) {
      const next = queue.current.shift()!
      inFlight.current.add(next.id)
      producaoApi
        .material(shell, next.id, next.fresh)
        .then((st) => setStates((s) => ({ ...s, [next.id]: st })))
        .catch((err) =>
          setStates((s) => ({
            ...s,
            [next.id]: { status: "erro", missing_count: 0, lines: [], error: String(err), checked_at: "" },
          })),
        )
        .finally(() => {
          inFlight.current.delete(next.id)
          pump()
        })
    }
  }, [shell])

  const request = useCallback(
    (jobId: string, opts?: { fresh?: boolean }) => {
      if (!opts?.fresh && requested.current.has(jobId)) return
      requested.current.add(jobId)
      queue.current.push({ id: jobId, fresh: !!opts?.fresh })
      pump()
    },
    [pump],
  )

  const get = useCallback((jobId: string) => states[jobId], [states])

  return <MaterialCtx.Provider value={{ get, request }}>{children}</MaterialCtx.Provider>
}

export function useMaterial(jobId: string, enabled = true): MaterialState | undefined {
  const ctx = useContext(MaterialCtx)
  useEffect(() => {
    if (enabled) ctx?.request(jobId)
  }, [ctx, jobId, enabled])
  return ctx?.get(jobId)
}

export function useMaterialRefresh(): (jobId: string) => void {
  const ctx = useContext(MaterialCtx)
  return useCallback((jobId: string) => ctx?.request(jobId, { fresh: true }), [ctx])
}
